import React from "react"
import { Button } from "./ui/button"
import { currencySymbol } from "../lib/currencies"
import { cn } from "../lib/utils"

const CELL_INPUT =
  "h-9 w-full rounded-lg border border-input bg-card px-2.5 text-sm text-foreground outline-none transition-shadow focus:border-primary focus:ring-2 focus:ring-ring/20"

const TH =
  "px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap"
const TD = "px-4 py-2.5 align-middle"

function parseAmount(value) {
  const n = Number.parseFloat(String(value ?? "0").trim())
  return Number.isNaN(n) ? 0 : n
}

function lineSubtotalCents(row) {
  const qty = parseAmount(row.quantity ?? "1")
  const unit = parseAmount(row.unit_price)
  return Math.max(0, Math.round(qty * unit * 100))
}

function lineDiscountCents(row) {
  return Math.max(0, Math.round(parseAmount(row.discount) * 100))
}

export function lineGrandTotalCents(row) {
  return Math.max(0, lineSubtotalCents(row) - lineDiscountCents(row))
}

function formatMoney(cents, currency = "USD") {
  const symbol = currencySymbol(currency)
  return `${symbol}${(cents / 100).toFixed(2)}`
}

/** value: [{ id?, description, quantity, unit_price, discount }] */
export function QuoteLineRows({ value, onChange, currency = "USD" }) {
  const emptyRow = { description: "", quantity: "1", unit_price: "", discount: "" }
  const rows = value?.length ? value : [emptyRow]

  function patchRow(i, patch) {
    onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)))
  }

  function addRow() {
    onChange([...rows, { ...emptyRow }])
  }

  function removeRow(i) {
    if (rows.length <= 1) {
      onChange([{ ...emptyRow }])
      return
    }
    onChange(rows.filter((_, j) => j !== i))
  }

  const grandTotalCents = rows.reduce((sum, row) => sum + lineGrandTotalCents(row), 0)

  return (
    <div className="md:col-span-3 grid gap-3">
      <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-sm">
        <table className="w-full min-w-[760px] table-fixed border-collapse text-sm">
          <colgroup>
            <col style={{ width: "56px" }} />
            <col />
            <col style={{ width: "132px" }} />
            <col style={{ width: "132px" }} />
            <col style={{ width: "132px" }} />
            <col style={{ width: "88px" }} />
          </colgroup>
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className={cn(TH, "text-center")}>S.No</th>
              <th className={cn(TH, "text-left")}>Item description</th>
              <th className={cn(TH, "text-right")}>Unit rate</th>
              <th className={cn(TH, "text-right")}>Discount</th>
              <th className={cn(TH, "text-right")}>Grand total</th>
              <th className={cn(TH, "text-center")} aria-label="Row actions" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const grandTotal = lineGrandTotalCents(row)
              return (
                <tr key={row.id ?? `new-${i}`} className="border-b border-border/70 last:border-b-0">
                  <td className={cn(TD, "text-center text-muted-foreground tabular-nums")}>{i + 1}</td>
                  <td className={TD}>
                    <input
                      type="text"
                      value={row.description ?? ""}
                      onChange={(e) => patchRow(i, { description: e.target.value })}
                      placeholder="Service or item description"
                      className={CELL_INPUT}
                    />
                  </td>
                  <td className={TD}>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={row.unit_price ?? ""}
                      onChange={(e) => patchRow(i, { unit_price: e.target.value })}
                      placeholder="0.00"
                      className={cn(CELL_INPUT, "text-right tabular-nums")}
                    />
                  </td>
                  <td className={TD}>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={row.discount ?? ""}
                      onChange={(e) => patchRow(i, { discount: e.target.value })}
                      placeholder="0.00"
                      className={cn(CELL_INPUT, "text-right tabular-nums")}
                    />
                  </td>
                  <td className={cn(TD, "text-right")}>
                    <div className="flex h-9 items-center justify-end font-medium tabular-nums text-foreground">
                      {formatMoney(grandTotal, currency)}
                    </div>
                  </td>
                  <td className={cn(TD, "text-center")}>
                    <div className="flex h-9 items-center justify-center">
                      <Button
                        type="button"
                        variant="ghost"
                        className="h-9 px-2 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
                        onClick={() => removeRow(i)}
                      >
                        Remove
                      </Button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-border bg-muted/40">
              <td className={TD} />
              <td colSpan={3} className={cn(TD, "text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground")}>
                Quote total
              </td>
              <td className={cn(TD, "text-right text-sm font-bold tabular-nums text-foreground")}>
                <div className="flex h-9 items-center justify-end">{formatMoney(grandTotalCents, currency)}</div>
              </td>
              <td className={TD} />
            </tr>
          </tfoot>
        </table>
      </div>
      <div>
        <Button type="button" variant="outline" className="h-9 px-3 text-xs" onClick={addRow}>
          + Add line
        </Button>
      </div>
    </div>
  )
}

export function quoteLinesTotalCents(lines) {
  return (lines || []).reduce((sum, row) => sum + lineGrandTotalCents(row), 0)
}
